import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { createHeaderScrollState, updateHeaderScrollState } from '../../lib/collapsingHeader'

const MobileChromeContext = createContext(null)

export function MobileChromeProvider({ routeKey, children }) {
  const [collapsed, setCollapsed] = useState(false)
  const scrollStateRef = useRef(createHeaderScrollState())
  const frameRef = useRef(null)

  const reveal = useCallback(() => {
    const scrollY = window.scrollY
    scrollStateRef.current = createHeaderScrollState(scrollY)
    setCollapsed(false)
  }, [])

  const updateFromScroll = useCallback((scrollY) => {
    const next = updateHeaderScrollState(scrollStateRef.current, scrollY)
    scrollStateRef.current = next
    setCollapsed(current => current === next.collapsed ? current : next.collapsed)
  }, [])

  useEffect(() => {
    reveal()
  }, [routeKey, reveal])

  useEffect(() => {
    const onScroll = () => {
      if (frameRef.current != null) return

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null
        updateFromScroll(window.scrollY)
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frameRef.current != null) window.cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [updateFromScroll])

  return (
    <MobileChromeContext.Provider value={{ collapsed, reveal, updateFromScroll }}>
      {children}
    </MobileChromeContext.Provider>
  )
}

export function useMobileChrome() {
  const context = useContext(MobileChromeContext)
  if (!context) throw new Error('useMobileChrome must be used within MobileChromeProvider')
  return context
}

export function MobileScrollRegion({ children, className = '', sheet = false, onScroll, ...props }) {
  const context = useContext(MobileChromeContext)

  const handleScroll = (event) => {
    onScroll?.(event)
    if (sheet) context?.updateFromScroll(event.currentTarget.scrollTop)
  }

  return (
    <div
      {...props}
      data-mobile-scroll-region={sheet ? 'sheet' : 'page'}
      onScroll={handleScroll}
      className={`${sheet ? 'mobile-scroll-region mobile-scroll-region--sheet' : 'mobile-scroll-region'} ${className}`.trim()}
    >
      {children}
    </div>
  )
}
